<!-- 
Panel for showing levy calculations.

This component is used for showing the levy calculation to internal staff (on the levy collection provider page),
to providers (on their levy estimator page) and to the public (on the public levy estimator).
When shown to the public and to providers, extra supporting text is shown. And when shown to providers on their
levy estimator page, it will also show up/down indicators to tell providers what affect fiddling with their
levy parameters has on the estimated levy.
-->
<template>
  <div>
    <div v-if="levyCalculation.totalLevy != null" class="mt-3">
      <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
      <div v-if="isLevyEstimator" v-html="templates.Introduction"/>
      <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
      <div v-if="isPublicLevyEstimator" v-html="templates.PublicEstimatorIntroduction"/>

      <levy-calculation-panel-totals 
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        @click:openPanel="openPanel"
      />

      <v-container fluid class="pa-0">
        <v-layout row wrap>
          <v-flex xs12 sm6>
            <v-tooltip v-if="isInternal" bottom>
              <v-btn 
                slot="activator" 
                :to="`/levy-estimator/${levyCollectionProvider.estimatorKey}`"
              >
                <v-icon class="mr-2">streetview</v-icon>
                Show provider's levy estimator
              </v-btn>
              <span>
                Show the provider's view of their levy calculations, as they would see if they clicked on the link in their levy
                estimation email. 
              </span>
            </v-tooltip>
            <v-btn  
              v-if="isLevyEstimator && !playgroundIsActive"
              large color="primary"
              @click="showPlaygroundDialog = true"
            >
              <v-icon class="mr-2">tune</v-icon>
              Levy estimator
            </v-btn>
            <playground-dialog
              v-if="isLevyEstimator"
              v-model="showPlaygroundDialog"  
              :estimator-key="estimatorKey"
              :levy-collection="levyCollection"
            />
          </v-flex>
          <v-flex xs12 sm6 class="text-xs-left text-sm-right pr-2 pb-2">
            <v-btn :disabled="allExpanded" flat class="mr-0" @click="expandAll"><v-icon>unfold_more</v-icon> Expand all</v-btn>
            <v-btn :disabled="allCollapsed" flat class="ml-2 mr-0" @click="collapseAll"><v-icon>unfold_less</v-icon> Collapse all</v-btn>
          </v-flex>
          <v-flex v-if="isLevyEstimator && !playgroundIsActive" xs12>
            <v-alert 
              :value="true" type="success" icon="new_releases" outline 
              color="secondary"
              class="d-inline-flex ml-2 mt-2 mb-3"
            >
              <span class="font-weight-medium">Tip: </span>
              Use the levy estimator to get a feel for how the levy calculation works and how the levy amounts may change as your circumstances shift.
            </v-alert>
          </v-flex>
        </v-layout>
      </v-container>
    </div>
    <v-divider v-if="hasTotal"/>

    <div v-if="hasTotal">
      <levy-calculation-panel-administrative-fee 
        v-if="!administrationFeeExempt"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelAdministrativeFeeBreakdown)"
        :panel-number="getPanelNumber(panelAdministrativeFeeBreakdown)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />

      <v-divider v-if="!administrationFeeExempt" />

      <levy-calculation-panel-risk-rated-premium 
        v-if="!riskRatingExempt"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelRiskRatedPremiumBreakdown)"
        :panel-number="getPanelNumber(panelRiskRatedPremiumBreakdown)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />

      <v-divider v-if="!riskRatingExempt" />

      <levy-calculation-panel-financial-strength 
        v-if="showScores"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelFinancialStrengthScore)"
        :panel-number="getPanelNumber(panelFinancialStrengthScore)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />

      <v-divider v-if="showScores" />

      <levy-calculation-panel-net-profit-ratio 
        v-if="showFinancialStrengthSubScores"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelNetProfitRatioScore)"
        :panel-number="getPanelNumber(panelNetProfitRatioScore)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />

      <v-divider v-if="showFinancialStrengthSubScores" />

      <levy-calculation-panel-return-on-assets 
        v-if="showFinancialStrengthSubScores"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelReturnOnAssetsScore)"
        :panel-number="getPanelNumber(panelReturnOnAssetsScore)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />

      <v-divider v-if="showFinancialStrengthSubScores" />

      <levy-calculation-panel-debt-to-equity
        v-if="showFinancialStrengthSubScores"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelDebtToEquityScore)"
        :panel-number="getPanelNumber(panelDebtToEquityScore)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />

      <v-divider v-if="showFinancialStrengthSubScores" />

      <levy-calculation-panel-completion-rate 
        v-if="showScores"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelCourseCompletionScore)"
        :panel-number="getPanelNumber(panelCourseCompletionScore)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />

      <v-divider v-if="showScores" />

      <levy-calculation-panel-non-compliance 
        v-if="showScores"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelNonComplianceScore)"
        :panel-number="getPanelNumber(panelNonComplianceScore)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />
      
      <v-divider v-if="showScores" />

      <levy-calculation-panel-late-payment
        v-if="showNonComplianceHistorySubScores"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelLatePaymentScore)"
        :panel-number="getPanelNumber(panelLatePaymentScore)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />

      <v-divider v-if="showNonComplianceHistorySubScores" />

      <levy-calculation-panel-short-registration
        v-if="showNonComplianceHistorySubScores"
        :levy-collection="levyCollection"
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelShortRegistrationScore)"
        :panel-number="getPanelNumber(panelShortRegistrationScore)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />
      
      <v-divider v-if="showNonComplianceHistorySubScores" />

      <levy-calculation-panel-special-tuition-protection 
        v-if="hasTotal && !specialTuitionProtectionExempt"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelSpecialTuitionProtectionBreakdown)"
        :panel-number="getPanelNumber(panelSpecialTuitionProtectionBreakdown)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />

      <v-divider v-if="hasTotal && !specialTuitionProtectionExempt" />

      <levy-calculation-panel-levy-cap 
        v-if="hasTotal && hasLevyCap"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelLevyCap)"
        :panel-number="getPanelNumber(panelLevyCap)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
      />

      <v-divider v-if="hasTotal && hasLevyCap" />

      <levy-calculation-panel-parameters 
        v-if="!playgroundIsActive && !isPublicLevyEstimator"
        :levy-collection="levyCollection" 
        :levy-collection-provider="levyCollectionProvider" 
        :mode="mode"
        :expanded="isExpanded(panelParameters)"
        :panel-number="getPanelNumber(panelParameters)"
        @click:openPanel="openPanel"
        @click:togglePanel="togglePanel"
        @click:showPlayground="showPlaygroundDialog = true"
      />

      <div v-else class="mt-5">&nbsp;</div>

    </div>

    <v-snackbar
      :value="playgroundIsActive && !showPlaygroundDialog"
      :timeout="0"
      bottom right multi-line auto-height
      color="info"
    >
      Showing calculations based on parameters you entered in the levy estimator.
      <v-btn flat @click="showPlaygroundDialog = true">
        Edit
      </v-btn>
      <v-btn flat @click="quitPlayground">
        Quit
      </v-btn>
    </v-snackbar>
    <v-snackbar
      :value="isPublicLevyEstimator"
      :timeout="0"
      bottom right multi-line auto-height
      color="info"
    >
      This levy calculation is based on the parameters you entered.
      <v-btn flat @click="$emit('click:edit')">
        Edit parameters
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import Vue from "vue";
import mixins from "./mixins";
import filterMixins from "@/mixins/filter.mixins";
import * as estimatorMutation from "@/store/levy-estimator/mutation-types";
import PlaygroundDialog from "@/components/levy-collection-providers/calculation/PlaygroundDialog.vue";
import LevyCalculationPanelTotals from "./LevyCalculationPanelTotals.vue";
import LevyCalculationPanelAdministrativeFee from "./LevyCalculationPanelAdministrativeFee.vue";
import LevyCalculationPanelRiskRatedPremium from "./LevyCalculationPanelRiskRatedPremium.vue";
import LevyCalculationPanelFinancialStrength from "./LevyCalculationPanelFinancialStrength.vue";
import LevyCalculationPanelNetProfitRatio from "./LevyCalculationPanelNetProfitRatio.vue";
import LevyCalculationPanelReturnOnAssets from "./LevyCalculationPanelReturnOnAssets.vue";
import LevyCalculationPanelDebtToEquity from "./LevyCalculationPanelDebtToEquity.vue";
import LevyCalculationPanelCompletionRate from "./LevyCalculationPanelCompletionRate.vue";
import LevyCalculationPanelNonCompliance from "./LevyCalculationPanelNonCompliance.vue";
import LevyCalculationPanelLatePayment from "./LevyCalculationPanelLatePayment.vue";
import LevyCalculationPanelShortRegistration from "./LevyCalculationPanelShortRegistration.vue";
import LevyCalculationPanelSpecialTuitionProtection from "./LevyCalculationPanelSpecialTuitionProtection.vue";
import LevyCalculationPanelLevyCap from "./LevyCalculationPanelLevyCap.vue";
import LevyCalculationPanelParameters from "./LevyCalculationPanelParameters.vue";

export default {
  components: {
    PlaygroundDialog,
    LevyCalculationPanelTotals,
    LevyCalculationPanelAdministrativeFee,
    LevyCalculationPanelRiskRatedPremium,
    LevyCalculationPanelFinancialStrength,
    LevyCalculationPanelNetProfitRatio,
    LevyCalculationPanelReturnOnAssets,
    LevyCalculationPanelDebtToEquity,
    LevyCalculationPanelCompletionRate,
    LevyCalculationPanelNonCompliance,
    LevyCalculationPanelLatePayment,
    LevyCalculationPanelShortRegistration,
    LevyCalculationPanelSpecialTuitionProtection,
    LevyCalculationPanelLevyCap,
    LevyCalculationPanelParameters
  },
  mixins: [filterMixins, mixins],
  data() {
    return {
      showPlaygroundDialog: false,
      panelStatuses: []
    };
  },
  computed: {
    allExpanded() {
      return this.panelStatuses.every(p => p);
    },
    allCollapsed() {
      return this.panelStatuses.every(p => !p);
    },
    panels() {
      // if altering these, make sure you keep them consistent with the panel numbering on the mixin's data()
      let current = 0;
      const numbers = [];
      const admin = this.administrationFeeExempt ? "" : current++;
      numbers.push(admin);

      const baseRisk = this.riskRatingExempt ? "" : current++;
      numbers.push(baseRisk);

      const financialStrength = !this.showScores ? "" : current++;
      numbers.push(financialStrength);

      const npr = !this.showFinancialStrengthSubScores ? "" : current++;
      numbers.push(npr);

      const roa = !this.showFinancialStrengthSubScores ? "" : current++;
      numbers.push(roa);

      const debt = !this.showFinancialStrengthSubScores ? "" : current++;
      numbers.push(debt);

      const completionScore = !this.showScores ? "" : current++;
      numbers.push(completionScore);

      const nonComplianceScore = !this.showScores ? "" : current++;
      numbers.push(nonComplianceScore);

      const latePaymentScore = !this.showNonComplianceHistorySubScores ? "" : current++;
      numbers.push(latePaymentScore);

      const shortRegistrationPeriodScore = !this.showNonComplianceHistorySubScores ? "" : current++;
      numbers.push(shortRegistrationPeriodScore);

      const special = this.specialTuitionProtectionExempt ? "" : current++;
      numbers.push(special);

      const cap = !this.hasLevyCap ? "" : current++;
      numbers.push(cap);

      const parameters = current++;
      numbers.push(parameters);

      return numbers;
    },
    panelCount() {
      return this.panels.filter(p => p !== "").length;
    }
  },
  mounted() {
    this.setupPanels();
  },
  methods: {
    setupPanels() {
      this.panelStatuses.length = this.panelCount;
      this.collapseAll();
    },
    getPanelNumber(n) {
      return this.panels[n - 1];
    },
    isExpanded(n) {
      return !!this.panelStatuses[this.getPanelNumber(n)];
    },
    togglePanel(n) {
      const panelNumber = this.getPanelNumber(n);
      if (this.panelStatuses[panelNumber]) {
        Vue.set(this.panelStatuses, panelNumber, false);
      } else {
        this.openPanel(n);
      }
    },
    openPanel(n) {
      const panelNumber = this.getPanelNumber(n);
      Vue.set(this.panelStatuses, panelNumber, true);
      const event = new Event("resize");
      dispatchEvent(event);

      this.$nextTick(function() {
        this.$vuetify.goTo(`#focus-${panelNumber}`, { container: ".scroll-container", offset: 100 });
        var el = document.getElementById(`focus-${panelNumber}`);
        el.tabIndex = "0";
        el.focus();
      });
    },
    expandAll() {
      this.setPanelStatus(true);
    },
    collapseAll() {
      this.setPanelStatus(false);
    },
    setPanelStatus(open) {
      for (let i = 0; i < this.panelCount; i++) {
        Vue.set(this.panelStatuses, i, open);
      }
    },
    quitPlayground() {
      this.$store.commit(estimatorMutation.PLAYGROUND_EDIT_QUIT);
    }
  }
};
</script>

<style lang="scss">
.i-link {
  text-decoration: none;
}
.section-heading {
  position: relative;
  text-decoration: none;
  color: #333;
  margin-bottom: 1px;
}
.section-heading:hover,
.section-heading:active {
  outline-width: 1px;
}
.section-heading:focus {
  background-color: var(--v-accent-base);
}
.focus-button {
  position: absolute !important;
  top: 5px;
  right: 7px;
}
.sub-breakdown {
  font-size: 21px !important;
  margin-left: 12px;
}
.financial-strength-breakdown {
  font-size: 19px !important;
  margin-left: 38px;
}
.non-compliance-history-breakdown {
  font-size: 19px !important;
  margin-left: 38px;
}
</style>